import { supabase } from '@/lib/supabase';
import type { TrafficSource } from '@/services/trafficStats';

export type TrafficPage = 'landing' | 'revista' | 'precios';
type Certainty = 'high' | 'medium' | 'low';

interface Attribution {
  source: TrafficSource;
  certainty: Certainty;
  referrerHost: string | null;
  sessionId: string;
}

interface SessionRecord {
  id: string;
  ts: number;
}

interface LastPageRecord {
  page: TrafficPage;
  at: number;
  sessionId: string;
}

const SESSION_KEY = 'fv_traffic_session';
const ATTRIBUTION_KEY = 'fv_traffic_attribution';
const LAST_KEY = 'fv_traffic_last_page';

/** Ventana de sesión y de anti-duplicado (debe coincidir con el RPC). */
const SESSION_TTL_MS = 30 * 60 * 1000;
const DEDUPE_MS = SESSION_TTL_MS;

const VALID_SOURCES: TrafficSource[] = [
  'facebook',
  'instagram',
  'google_ads',
  'google',
  'directo',
  'otros',
  'desconocido',
];

const FACEBOOK_HOSTS = [
  'facebook.com',
  'm.facebook.com',
  'web.facebook.com',
  'mbasic.facebook.com',
  'l.facebook.com',
  'lm.facebook.com',
  'fb.com',
  'fb.me',
];

const INSTAGRAM_HOSTS = ['instagram.com', 'l.instagram.com', 'ig.me'];

const PAID_GOOGLE_HOSTS = [
  'googleadservices.com',
  'doubleclick.net',
  'googlesyndication.com',
  'googleads.g.doubleclick.net',
];

const PAID_UTM_MEDIUMS = ['cpc', 'ppc', 'paid', 'ads', 'adwords', 'display', 'google_ads', 'sem'];

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage no disponible (modo privado estricto): se sigue sin persistir
  }
}

function isTrafficSource(value: unknown): value is TrafficSource {
  return typeof value === 'string' && VALID_SOURCES.includes(value as TrafficSource);
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Identificador de sesión compartido entre pestañas del mismo navegador.
 *
 * Vive en localStorage (no en sessionStorage) para que abrir otra pestaña NO
 * cuente como un visitante nuevo. Se renueva solo tras 30 minutos sin actividad,
 * que es la misma ventana que usa el anti-duplicado del RPC.
 */
function getOrCreateSessionId(): string | null {
  if (typeof window === 'undefined') return null;

  const now = Date.now();
  const stored = readJson<SessionRecord>(SESSION_KEY);

  if (
    stored &&
    typeof stored.id === 'string' &&
    stored.id.length >= 8 &&
    stored.id.length <= 64 &&
    typeof stored.ts === 'number'
  ) {
    if (now - stored.ts <= SESSION_TTL_MS) {
      writeJson(SESSION_KEY, { id: stored.id, ts: now });
      return stored.id;
    }
    const renewed = generateSessionId();
    writeJson(SESSION_KEY, { id: renewed, ts: now });
    return renewed;
  }

  const created = generateSessionId();
  writeJson(SESSION_KEY, { id: created, ts: now });
  return created;
}

function hostFromReferrer(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
}

function matchHost(host: string, list: string[]): boolean {
  return list.some((item) => host === item || host.endsWith(`.${item}`));
}

function isGoogleHost(host: string): boolean {
  return /(^|\.)google\.[a-z.]+$/.test(host) || host === 'google.com';
}

function isPaidGoogleHost(host: string): boolean {
  return matchHost(host, PAID_GOOGLE_HOSTS);
}

function normalizeUtmSource(value: string): TrafficSource | null {
  const v = value.trim().toLowerCase();
  if (!v) return null;
  if (v.includes('facebook') || v === 'fb' || v === 'meta' || v.includes('meta_ads')) return 'facebook';
  if (v.includes('instagram') || v === 'ig' || v.includes('insta')) return 'instagram';
  if (v.includes('google') || v === 'adwords' || v === 'gads') return 'google';
  return 'otros';
}

/**
 * Clasifica el origen de la visita con la evidencia disponible.
 *
 * Reglas (en este orden):
 *  1. Clic publicitario de Google (gclid / gbraid / wbraid / dclid / gad_source)
 *     => google_ads, certeza alta.
 *  2. utm_source presente => según valor; si apunta a Google se separa
 *     google_ads (utm_medium cpc/ppc/paid/ads…) de google orgánico.
 *  3. Referrer de red publicitaria de Google o URL con /aclk
 *     => google_ads, certeza media.
 *  4. Referrer reconocible (Facebook / Instagram / Google) => certeza media.
 *     Google por referrer se considera orgánico (google).
 *  5. Referrer del propio sitio => Directo (solo cuando no hay origen previo guardado).
 *  6. Referrer vacío => Desconocido (nunca se adivina).
 *  7. Cualquier otro dominio => Otros.
 */
export function classifyTrafficSource(
  search: string,
  referrer: string,
): { source: TrafficSource; certainty: Certainty; referrerHost: string | null } {
  const params = new URLSearchParams(search);
  const referrerHost = hostFromReferrer(referrer);
  const lowerReferrer = referrer.toLowerCase();
  const utmMedium = (params.get('utm_medium') ?? '').trim().toLowerCase();

  // 1) Clic publicitario de Google
  if (
    params.get('gclid') ||
    params.get('gbraid') ||
    params.get('wbraid') ||
    params.get('dclid') ||
    params.get('gad_source')
  ) {
    return { source: 'google_ads', certainty: 'high', referrerHost };
  }

  // 2) utm_source / utm_medium
  const utm = normalizeUtmSource(params.get('utm_source') ?? '');
  if (utm === 'google') {
    const paid = PAID_UTM_MEDIUMS.includes(utmMedium);
    return { source: paid ? 'google_ads' : 'google', certainty: 'medium', referrerHost };
  }
  if (utm) {
    return { source: utm, certainty: 'medium', referrerHost };
  }

  // 3) Referrer de red publicitaria de Google (sin parámetros)
  if (referrerHost && isPaidGoogleHost(referrerHost)) {
    return { source: 'google_ads', certainty: 'medium', referrerHost };
  }
  if (lowerReferrer.includes('/aclk') || lowerReferrer.includes('/pagead/aclk')) {
    return { source: 'google_ads', certainty: 'medium', referrerHost };
  }

  if (!referrerHost) {
    return { source: 'desconocido', certainty: 'low', referrerHost };
  }

  // 4) Referrer reconocible
  if (matchHost(referrerHost, FACEBOOK_HOSTS)) {
    return { source: 'facebook', certainty: 'medium', referrerHost };
  }
  if (matchHost(referrerHost, INSTAGRAM_HOSTS)) {
    return { source: 'instagram', certainty: 'medium', referrerHost };
  }
  if (isGoogleHost(referrerHost)) {
    return { source: 'google', certainty: 'medium', referrerHost };
  }

  // 5) Navegación interna sin origen previo
  if (referrerHost === window.location.hostname.toLowerCase()) {
    return { source: 'directo', certainty: 'medium', referrerHost };
  }

  // 6) Otro dominio
  return { source: 'otros', certainty: 'high', referrerHost };
}

function readAttribution(): Attribution | null {
  const raw = readJson<Attribution>(ATTRIBUTION_KEY);
  if (!raw) return null;
  if (!isTrafficSource(raw.source)) return null;
  if (raw.certainty !== 'high' && raw.certainty !== 'medium' && raw.certainty !== 'low') return null;
  if (typeof raw.sessionId !== 'string' || !raw.sessionId) return null;
  return raw;
}

function writeAttribution(attribution: Attribution): void {
  writeJson(ATTRIBUTION_KEY, attribution);
}

/**
 * El origen se calcula UNA vez por sesión y se reutiliza en toda la navegación.
 *
 *  - Si la sesión sigue viva: se devuelve el origen guardado.
 *  - Si la sesión se renovó pero el visitante llegó desde una página del propio
 *    sitio (recarga o navegación interna), se conserva el origen anterior:
 *    una navegación interna NUNCA se convierte en "Directo" por sí sola.
 *  - Si la sesión se renovó y hay referrer externo, se reclasifica.
 */
function resolveAttribution(sessionId: string): Attribution {
  const stored = readAttribution();
  if (stored && stored.sessionId === sessionId) {
    return stored;
  }

  const referrer = typeof document === 'undefined' ? '' : document.referrer;
  const referrerHost = hostFromReferrer(referrer);
  const ownHost = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();

  if (stored && referrerHost && referrerHost === ownHost) {
    const carried: Attribution = { ...stored, sessionId };
    writeAttribution(carried);
    return carried;
  }

  const fresh: Attribution = {
    ...classifyTrafficSource(typeof window === 'undefined' ? '' : window.location.search, referrer),
    sessionId,
  };
  writeAttribution(fresh);
  return fresh;
}

/**
 * Se llama una vez al arrancar la aplicación (antes del router).
 * Congela el origen real de entrada, aunque el visitante primero pase por
 * la portada y llegue a /revista o /precios mediante navegación interna.
 */
export function seedTrafficAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return;
    resolveAttribution(sessionId);
  } catch {
    // La analítica nunca debe romper la carga de la aplicación
  }
}

function readLastPage(): LastPageRecord | null {
  const raw = readJson<LastPageRecord>(LAST_KEY);
  if (!raw) return null;
  if (raw.page !== 'landing' && raw.page !== 'revista' && raw.page !== 'precios') return null;
  if (typeof raw.at !== 'number' || typeof raw.sessionId !== 'string') return null;
  return raw;
}

/**
 * Registra la visita a / (landing), /revista o /precios.
 * Best-effort: si falla, nunca rompe la página.
 */
export async function trackTrafficVisit(page: TrafficPage): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return;

    const now = Date.now();
    const last = readLastPage();

    // Deduplicación local: mismos re-render, recarga o regreso accidental
    if (last && last.sessionId === sessionId && last.page === page && now - last.at < DEDUPE_MS) {
      return;
    }

    // La página anterior solo cuenta como "paso interno" si es de esta sesión
    // y ocurrió dentro de la ventana: así una visita suelta horas después no
    // se cuenta como navegación Revista -> Precios.
    const previousPage: TrafficPage | null =
      last &&
      last.sessionId === sessionId &&
      last.page !== page &&
      now - last.at <= DEDUPE_MS
        ? last.page
        : null;

    const attribution = resolveAttribution(sessionId);

    writeJson(LAST_KEY, { page, at: now, sessionId });

    const { error } = await supabase.rpc('track_traffic_event', {
      p_page: page,
      p_source: attribution.source,
      p_source_certainty: attribution.certainty,
      p_referrer_host: attribution.referrerHost,
      p_previous_page: previousPage,
      p_session_id: sessionId,
    });

    if (error) {
      // Se ignora: las estadísticas no deben afectar la navegación del visitante
      console.warn('[traffic] No se pudo registrar la visita:', error.message);
    }
  } catch {
    console.warn('[traffic] Error inesperado al registrar la visita');
  }
}